import { Component, TemplateFunction } from '@ribajs/core'
import { PopupState } from '../types/index.js'
import type { SbPopupComponent } from './sb-popup.component.js'

interface Scope {
  // Options
  /** The target popup id */
  target: string
  /** True if the target popup is visible */
  visible: boolean
  /** True if the target popup is hidden */
  hidden: boolean
  /** Additional data which is sent to the popup on show, you can use this to send additional data to the popup scope from the context of the popup button */
  data?: any
  // Methods
  /** Hides the target popup */
  hide: SbPopupButtonComponent['hide']
  /** Shows the target popup */
  show: SbPopupButtonComponent['show']
  /** Toggle (hide/show) the target popup */
  toggle: SbPopupButtonComponent['toggle']
}

/**
 * A custom popup button element to toggle any popup by `target`
 * @note: This component is similar to the sb-sidebar-button component
 */
export class SbPopupButtonComponent extends Component {
  public static tagName = 'sb-popup-button'

  protected popup?: SbPopupComponent

  static get observedAttributes(): string[] {
    return ['target', 'data']
  }

  protected requiredAttributes(): string[] {
    return ['target']
  }

  public scope: Scope = {
    // Options
    /** The target popup id */
    target: '',
    visible: false,
    hidden: true,
    data: {},
    // Methods
    hide: this.hide,
    show: this.show,
    toggle: this.toggle,
  }

  public getState() {
    return this.popup?.getState()
  }

  public hide(event?: Event) {
    if (!this.popup) {
      this.error(`Popup with id "${this.scope.target}" not found!`)
      return
    }
    event?.preventDefault()
    event?.stopPropagation()
    this.popup.hide(event)
  }

  public show(event?: Event) {
    if (!this.popup) {
      this.error(`Popup with id "${this.scope.target}" not found!`)
      return
    }
    event?.preventDefault()
    event?.stopPropagation()
    this.popup.show(event, this.scope.data, true)
  }

  public toggle(event?: Event) {
    if (!this.popup) {
      this.error(`Popup with id "${this.scope.target}" not found!`)
      return
    }
    event?.preventDefault()
    event?.stopPropagation()
    this.popup.toggle(event, this.scope.data, true)
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbPopupButtonComponent.observedAttributes)
  }

  protected _onStateChange(event: CustomEvent<PopupState>) {
    this.scope.visible = event.detail.visible
    this.scope.hidden = !this.scope.visible
  }
  onStateChange = this._onStateChange.bind(this)

  async afterBind() {
    await super.afterBind()
    this.initPopup()
    this.classList.add(`${SbPopupButtonComponent.tagName}-ready`)
  }

  protected initPopup() {
    this.popup =
      document.querySelector(`sb-popup[id='${this.scope.target}']`) || undefined
    if (!this.popup) {
      throw new Error(
        `sb-popup element with id "${this.scope.target}" not found!`,
      )
    }
    this.popup.removeEventListener('state-changed' as any, this.onStateChange)
    this.popup.addEventListener('state-changed' as any, this.onStateChange)
    this.scope.visible = this.popup.getState().visible
    this.scope.hidden = !this.scope.visible
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
